import classNames from 'classnames'
import merge from 'lodash/merge'
import React, {useEffect} from 'react'
import {Link} from 'react-router-dom'

import useMyId from '../../../../../../hooks/useMyId'
import {getBuildStatusIconFromBuild, getBuildStatusTypeFromBuild} from '../../../../../../selectors'
import {restApi} from '../../../../../../services/rest'
import type {BuildId, NormalizedBuild} from '../../../../../../types'
import {formatDuration} from '../../../../../../utils/dateTimeUtils'
import {subscribeOnRunningBuild} from '../../../../../../utils/subscriber'
import {isReused} from '../../../../BuildPage/DependenciesTab/DependenciesChain/DependenciesChain.utils'
import JobIconStatus from '../../../components/common/JobIconStatus/JobIconStatus'
import RunStatus from '../../../components/common/RunStatus/RunStatus'
import {useJobId} from '../../../EditPipelinePage/EditPipelinePage.hooks'
import {usePipelineRunArg} from '../../PipelineRunPage.hooks'

import PipelineRunJobProgress from './PipelineRunJobProgress/PipelineRunJobProgress'

import styles from './PipelineRunJob.css'

import {useAppDispatch} from 'src/hooks/react-redux'

type Props = {
  x: number
  y: number
  jobId: BuildId
}

export default function PipelineRunJob({x, y, jobId}: Props) {
  const arg = usePipelineRunArg()
  const {job, run} = restApi.endpoints.getBuildNormalized.useQuery(arg, {
    selectFromResult: ({data}) => {
      const run = data?.entities?.builds?.[data?.result!]

      return {
        job: run?.['snapshot-dependencies']?.build?.find(build => build.id === jobId),
        run,
      }
    },
  })
  const isJobReused = isReused(job as NormalizedBuild, run)
  const myId = useMyId()
  const icon = getBuildStatusIconFromBuild(job, myId)
  const {state, parallelized, buildType, 'running-info': runningInfo} = job ?? {}
  const {percentageComplete, leftSeconds} = runningInfo ?? {}
  const runStatusType = getBuildStatusTypeFromBuild(job)
  const dispatch = useAppDispatch()
  const selectedJob = Number(useJobId())
  const jonSnapshotDependenciesBuild = job?.['snapshot-dependencies']?.build

  useEffect(() => {
    if (state === 'running') {
      return subscribeOnRunningBuild(jobId, data =>
        dispatch(
          restApi.util.updateQueryData('getBuildNormalized', arg, draft => {
            const draftRun = draft?.entities?.builds?.[draft?.result!]?.[
              'snapshot-dependencies'
            ]?.build?.find(build => build.id === jobId)

            merge(draftRun, data)
          }),
        ),
      )
    }
    return undefined
  }, [arg, dispatch, jobId, state])

  return (
    <Link
      to={{search: jobId === selectedJob ? '' : `job=${jobId}`}}
      className={classNames(styles.tile, {
        [styles.running]: state === 'running',
        [styles.selected]: jobId === selectedJob,
      })}
      style={{top: x, left: y}}
    >
      <div className={styles.head}>
        <JobIconStatus parallelized={parallelized} icon={icon} status={job?.status} />
        {leftSeconds != null && (
          <span className={styles.left}>{`${formatDuration(leftSeconds)} left`}</span>
        )}
      </div>
      <div className={styles.name}>
        <span className={styles.text}>{buildType?.name}</span>
      </div>
      <div className={classNames(styles.status, styles[runStatusType])}>
        <RunStatus
          className={styles.text}
          {...job}
          reused={isJobReused}
          withLink={false}
          withIcon={false}
          omitUnknown
        />
      </div>
      {!!percentageComplete && (
        <PipelineRunJobProgress
          mainPercentage={percentageComplete}
          builds={jonSnapshotDependenciesBuild}
          status={runStatusType}
        />
      )}
    </Link>
  )
}
